import { JSX } from 'react';
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Guid from 'common/values/guid/guid';
import Document from "documents/entities/document/document";
import { AccessLevel, Resource, ResourceType, Scope } from 'permissions/entities/permission/resources/general';

export enum DocumentSubjectMatter {
  'Financial',
  'Legal',
  'Confidential',
}
export interface IGeneralDocumentAccessResource {
  isDisabled?: boolean;
  rawText?: string;
  accessLevel?: string;
  scope?: string;
}
export interface IDocumentContentsAccessResource {
  isDisabled?: boolean;
  rawText?: string;
  subjectMatter?: string;
  scope?: string;
}
export interface IDocumentAccessResource {
  isDisabled?: boolean;
  rawText?: string;
  documentId?: string;
  accessLevel?: string;
}

export class GeneralDocumentAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.GeneralDocumentAccess;
  public accessLevel?: AccessLevel;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `documents ${this.accessLevel} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Documents</strong><span>
      {`: ${this.scope} (${this.accessLevel})`}</span></>} />;
  }

  public override toString(): string {
    return `Documents: ${this.scope} (${this.accessLevel})`;
  }

  public fromObject(obj: IGeneralDocumentAccessResource): Resource {
    const resource = new GeneralDocumentAccess();
    resource.rawText = obj.rawText || "";
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.scope = Scope[obj.scope as keyof typeof Scope];
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): GeneralDocumentAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(GeneralDocumentAccess, fieldIterations) as GeneralDocumentAccess[];
  }
}

export class DocumentContentsAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.DocumentContentsAccess;
  public subjectMatter?: DocumentSubjectMatter;
  public scope?: Scope;
  public rawText?: string;

  public get metaText(): string {
    return `document contents ${this.subjectMatter} ${this.scope}`;
  }

  public asJSXElement(): JSX.Element {
    return <Chip label={<><strong>Document Contents</strong>
      <span>{`: ${this.subjectMatter} (${this.scope})`}</span></>} />;
  }

  public override toString(): string {
    return `Document Contents: ${this.subjectMatter} (${this.scope})`;
  }

  public fromObject(obj: IDocumentContentsAccessResource): DocumentContentsAccess {
    const resource = new DocumentContentsAccess();
    resource.rawText = obj.rawText || "";
    resource.subjectMatter = DocumentSubjectMatter[
      obj.subjectMatter as keyof typeof DocumentSubjectMatter];
    resource.scope = obj.scope ? Scope[obj.scope as keyof typeof Scope] : 0;
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      subjectMatter: this.subjectMatter ? DocumentSubjectMatter[this.subjectMatter] : null,
      scope: typeof this.scope === 'string' ? Scope[this.scope] : this.scope,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): DocumentContentsAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['subjectMatter', Object.keys(DocumentSubjectMatter).filter((key) => Number.isNaN(Number(key)))],
      ['scope', Object.keys(Scope).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(DocumentContentsAccess, fieldIterations) as DocumentContentsAccess[];
  }
}

export class DocumentAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.DocumentAccess;
  public documentId?: Guid;
  public accessLevel?: AccessLevel;
  public rawText?: string;

  public get metaText(): string {
    return `document ${this.documentId?.toString()} ${this.accessLevel}`;
  }

  public asJSXElement = (options: {
    document: Document;
    isLoadingDocument: boolean | undefined;
    errorLoadingDocument: string | null | undefined;
  }): JSX.Element => {
    if (options?.isLoadingDocument) {
      return <Chip label={<>
        <strong>Document</strong>
        <span>{`: Loading...`}</span></>}
      />;
    }

    if (options?.errorLoadingDocument) {
      return <>
        <Chip label={<>
          <strong>Document</strong>
          <span>
            {`: ${options?.document?.name || this.documentId?.toString()} (${this.accessLevel})`}
          </span>
        </>} />
        <small style={{ color: 'red', display: 'flex', alignItems: 'center' }}>
          <Icon color="error">error</Icon>
          {options?.errorLoadingDocument}
        </small>
      </>;
    }

    return <Chip label={<>
      <strong>Document</strong>
      <span>
        {`: ${options?.document?.name || this.documentId?.toString()} (${this.accessLevel})`}
      </span>
    </>} />;
  };
  
  public override toString(options: {
    document: Document;
    isLoadingDocument: boolean | undefined;
    errorLoadingDocument: string | null | undefined;
  }): string {
    if (options?.isLoadingDocument) {
      return `Document: Loading...`;
    }

    if (options?.errorLoadingDocument) {
      return `Document: ${this.documentId?.toString()} (${this.accessLevel}) - Error: ${options.errorLoadingDocument}`;
    }

    return `Document: ${options?.document?.name || this.documentId?.toString()} (${this.accessLevel})`;
  }

  public fromObject(obj: IDocumentAccessResource): Resource {
    const resource = new DocumentAccess();
    resource.rawText = obj.rawText || "";
    resource.documentId = obj.documentId ? new Guid(obj.documentId) : undefined;
    resource.accessLevel = obj.accessLevel ? AccessLevel[obj.accessLevel as keyof typeof AccessLevel] : undefined;
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      documentId: this.documentId?.toString(),
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      isDisabled: this.isDisabled
    });
  }
}
